import {
  type SpatialEntityFields,
  type Template,
  type TemplateWithData,
  type RendererResult,
  type TemplateType,
} from '@ekkogmbh/apisdk';
import { Button, Card, CardActions, CardContent, CardMedia, Collapse, List, Skeleton, Typography } from '@mui/material';
import { Error, ExpandLess, ExpandMore } from '@mui/icons-material';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useRequest from 'src/Common/Hooks/useRequest';
import useEkko from 'src/Common/Providers/EkkoProvider';
import { derivePreviewDisplay } from './previewDisplay';
import { DiagnosticItem } from './DiagnosticItem';

interface Props {
  extended?: boolean;
  fields?: SpatialEntityFields;
  preRender?: RendererResult;
  template?: Template | TemplateWithData;
}

export const TemplatePreview = ({ extended, template, fields, preRender }: Props): React.JSX.Element => {
  const { api } = useEkko();
  const renderTimeout = useRef<number>();
  const [isFetching, startFetching] = useRequest();
  const [, startRendering] = useRequest();

  const [data, setData] = useState<string>();
  const [type, setType] = useState<TemplateType>();
  const [rendering, setRendering] = useState<RendererResult>();
  const [isExtended, setExtended] = useState<boolean>(extended === true);

  useEffect(() => {
    if (preRender !== undefined) {
      setRendering(preRender);
    }
  }, [preRender]);

  const renderCallback = useCallback(() => {
    if (preRender !== undefined) {
      // just using the already rendered image if given
      // setRendering(preRender);
      return;
    }

    if (data === undefined || type === undefined) {
      return;
    }

    startRendering(
      () =>
        api.renderTemplate({
          data,
          type,
          fields,
          autoFill: fields === undefined,
          render: true,
        }),
      (result) => setRendering(result as RendererResult),
    );
  }, [data, type, fields, preRender]);

  /** Queue rendering with new data */
  useEffect(() => {
    // handling in a timeout to prevent fetches with quick field changes
    renderTimeout.current = window.setTimeout(renderCallback, 500);
    return () => {
      window.clearTimeout(renderTimeout.current);
    };
  }, [data, type, fields]);

  /** Extract or fetch info required for rendering */
  useEffect(() => {
    if (template === undefined) {
      return;
    }

    setType(template.type);
    if (template.data === undefined) {
      startFetching(
        () => api.getTemplate(template),
        (result) => setData((result as TemplateWithData).data),
      );
    } else {
      setData(template.data);
    }
  }, [template]);

  const { image, text, messages, isPending, isFailed } = useMemo(() => derivePreviewDisplay(rendering), [rendering]);

  const imageElement = useMemo(() => {
    if (isPending) {
      return <Skeleton variant={'rectangular'} animation={isFetching ? 'pulse' : 'wave'} height={200} />;
    }

    if (text !== undefined) {
      return <Typography sx={{ p: 2 }}>{text}</Typography>;
    }

    if (image === undefined) {
      return <></>;
    }

    return <CardMedia src={`data:image/png;base64,${image}`} component={'img'} />;
  }, [image, text, isPending, isFetching]);

  return (
    <Card elevation={3}>
      {imageElement}
      {(messages.length > 0 || isFailed) && (
        <>
          <CardContent style={{ textAlign: 'center' }}>
            <Error color={isFailed ? 'error' : undefined} />
            <Typography>{isFailed ? 'preview not available' : 'preview incomplete'}</Typography>
          </CardContent>
          <Collapse in={messages.length > 0 && isExtended} timeout={'auto'} unmountOnExit>
            <List disablePadding>
              {messages.map((message: string, index: number) => (
                <DiagnosticItem key={index} line={message} />
              ))}
            </List>
          </Collapse>
          <CardActions style={{ justifyContent: 'center' }}>
            <Button
              size="small"
              onClick={() => setExtended(!isExtended)}
              startIcon={isExtended ? <ExpandLess /> : <ExpandMore />}
              endIcon={isExtended ? <ExpandLess /> : <ExpandMore />}
            >
              Errors
            </Button>
          </CardActions>
        </>
      )}
    </Card>
  );
};
